import { put, takeLatest, takeEvery, select } from 'redux-saga/effects';
import uniqid from 'uniqid';

import { supportsSegmentationAndCompare } from 'src/utils/features';
import { isEmpty } from 'src/utils';
import {
  getTabsFromLocalStorage,
  setTabsInLocalStorage,
} from '../utils/TabsLocalStorage';

import {
  ADD_APPLICATION_TAB,
  REMOVE_APPLICATION_TAB,
  UPDATE_TABS,
  UPDATE_TAB_LOADING_STATUS,
  UPDATE_TAB_LOADING_STATUS_SUCCESS,
  SAVE_AUDIENCE_SUCCESS,
  UPDATE_TABS_FROM_COMPANY_SETTINGS,
  CHANGE_SELECTED_APPLICATION_TAB,
} from '../constants/action-types';
import { addToast } from '../actions';
import translations from '../lang';

function* handleAddApplicationTab(action) {
  const currentTabs = yield select(state => state.tabs.list);
  const currentLoggedUserEmail = yield select(state => state.me.data.email);
  const id = uniqid();

  if (
    !supportsSegmentationAndCompare(action?.audience?.metadata?.data_source) &&
    action?.appName === 'segmentation'
  ) {
    return yield put(
      addToast({
        status: 'error',
        message:
          translations['en-US'].TOASTS.SEGMENT_AUDIENCE_NOT_ALLOWED[
            action.audience.metadata.data_source.toUpperCase()
          ],
      })
    );
  }

  const updatedTabs = [
    ...currentTabs,
    {
      id,
      app: action.appName,
      audience: action.audience,
    },
  ];

  yield put({ type: UPDATE_TABS, tabs: updatedTabs, newSelectedTabId: id });
  setTabsInLocalStorage(updatedTabs, currentLoggedUserEmail);
}

const getNewSelectedTabId = (
  isRemovingTabSelected,
  currentSelectedTabId,
  removingTabIndex,
  currentTabs,
  updatedTabs
) => {
  if (!isRemovingTabSelected) {
    return currentSelectedTabId;
  }
  if (currentTabs.length === 1) {
    return null;
  }

  if (removingTabIndex === 0) {
    return updatedTabs[0].id;
  }

  return updatedTabs[removingTabIndex]
    ? updatedTabs[removingTabIndex].id
    : updatedTabs[removingTabIndex - 1].id;
};

function* handleRemoveApplicationTab(action) {
  const currentTabs = yield select(state => state.tabs.list);
  const currentSelectedTabId = yield select(state => state.tabs.selectedTabId);
  const currentLoggedUserEmail = yield select(state => state.me.data.email);
  const removingTabIndex = currentTabs.findIndex(tab => tab.id === action.id);
  const isRemovingTabSelected = action.id === currentSelectedTabId;

  const updatedTabs = currentTabs.filter(tab => tab.id !== action.id);
  const newSelectedTabId = getNewSelectedTabId(
    isRemovingTabSelected,
    currentSelectedTabId,
    removingTabIndex,
    currentTabs,
    updatedTabs
  );

  yield put({ type: UPDATE_TABS, tabs: updatedTabs, newSelectedTabId });
  setTabsInLocalStorage(updatedTabs, currentLoggedUserEmail);
}

function* handleOnTabsUpdated() {
  const currentTabReducer = yield select(state => state.tabs);
  const currentLoggedUserEmail = yield select(state => state.me.data.email);
  const cleanedFromLoaded = {
    ...currentTabReducer,
    list: currentTabReducer.list.map(tab => ({
      ...tab,
      loaded: false,
    })),
  };
  setTabsInLocalStorage(cleanedFromLoaded, currentLoggedUserEmail);
}

function* handleUpdateTabLoadingStatus(action) {
  const { tabId } = action;
  const currentTabsList = yield select(state => state.tabs.list);

  const updatedTabsList = currentTabsList.map(tab => {
    if (tab.id === tabId) {
      return {
        ...tab,
        loaded: true,
      };
    }

    return tab;
  });

  yield put({
    type: UPDATE_TAB_LOADING_STATUS_SUCCESS,
    tabs: updatedTabsList,
  });
}

function* handleOnQuerySaved(action) {
  const { savedId, data } = action;
  const currentTabsList = yield select(state => state.tabs.list);
  const currentLoggedUserEmail = yield select(state => state.me.data.email);

  const updatedTabsList = currentTabsList.map(tab => {
    if (parseInt(tab.audience.id) === parseInt(savedId)) {
      return {
        ...tab,
        audience: {
          ...tab.audience,
          label: data.label,
          persistence: 'permanent',
        },
      };
    }

    return tab;
  });
  setTabsInLocalStorage(updatedTabsList, currentLoggedUserEmail);

  yield put({
    type: UPDATE_TAB_LOADING_STATUS_SUCCESS,
    tabs: updatedTabsList,
  });
}

function* handleUpdateTabsFromCompanySettings(action) {
  const { sources } = action;
  const email = yield select(state => state.me.data.email);

  const localStorageTabs = getTabsFromLocalStorage(email);

  if (
    localStorageTabs &&
    Array.isArray(localStorageTabs) &&
    localStorageTabs.length
  ) {
    const availableTabs = localStorageTabs.filter(
      tab =>
        tab.app !== 'analyze' ||
        (tab.audience &&
          tab.audience.metadata &&
          sources.includes(tab.audience.metadata.data_source))
    );

    if (!isEmpty(availableTabs)) {
      yield put({
        type: UPDATE_TAB_LOADING_STATUS_SUCCESS,
        tabs: availableTabs,
      });
      yield put({
        type: CHANGE_SELECTED_APPLICATION_TAB,
        id: availableTabs[0].id,
      });
    }
    setTabsInLocalStorage(availableTabs, email);
  }
}

export function* tabsActionWatcher() {
  yield takeLatest(ADD_APPLICATION_TAB, handleAddApplicationTab);
  yield takeLatest(REMOVE_APPLICATION_TAB, handleRemoveApplicationTab);
  yield takeLatest(UPDATE_TABS, handleOnTabsUpdated);
  yield takeEvery(UPDATE_TAB_LOADING_STATUS, handleUpdateTabLoadingStatus);
  yield takeEvery(SAVE_AUDIENCE_SUCCESS, handleOnQuerySaved);
  yield takeLatest(
    UPDATE_TABS_FROM_COMPANY_SETTINGS,
    handleUpdateTabsFromCompanySettings
  );
}
